import React, { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
    LayoutDashboard, Radio, CloudRain, Wind, Flame, History as HistoryIcon,
    MapPin, Sliders, Menu, X, Activity, Sprout, RefreshCcw, Snowflake,
} from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { fetchSyncStatus, triggerSync } from "@/lib/api";
import { timeAgo } from "@/lib/risk";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

const NAV = [
    { to: "/", label: "Home", icon: LayoutDashboard, testid: "nav-home" },
    { to: "/stations", label: "Stations", icon: Radio, testid: "nav-stations" },
    { to: "/rainfall", label: "Rainfall", icon: CloudRain, testid: "nav-rainfall" },
    { to: "/spray", label: "Spray", icon: Wind, testid: "nav-spray" },
    { to: "/fire", label: "Fire", icon: Flame, testid: "nav-fire" },
    { to: "/frost-history", label: "Frost History", icon: Snowflake, testid: "nav-frost-history" },
    { to: "/history", label: "History", icon: HistoryIcon, testid: "nav-history" },
    { to: "/map", label: "Map", icon: MapPin, testid: "nav-map" },
    { to: "/settings", label: "Settings", icon: Sliders, testid: "nav-settings" },
];

function SidebarInner({ onNavigate }) {
    const { data: sync } = useQuery({
        queryKey: ["sync"],
        queryFn: fetchSyncStatus,
        refetchInterval: 30_000,
    });
    const qc = useQueryClient();
    const syncMut = useMutation({
        mutationFn: triggerSync,
        onSuccess: () => {
            qc.invalidateQueries();
            toast.success("Weather data refreshed");
        },
    });

    return (
        <div className="flex h-full flex-col bg-[var(--fw-nav-bg)] text-slate-300">
            <div className="flex items-center gap-2.5 px-5 py-6 border-b border-slate-800">
                <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-sky-400 to-emerald-500 grid place-items-center">
                    <Sprout className="h-5 w-5 text-slate-900" strokeWidth={2.5} />
                </div>
                <div>
                    <div className="text-white font-bold text-lg leading-none" style={{ fontFamily: "Outfit" }}>
                        Malabar Weather
                    </div>
                </div>
            </div>

            <nav className="flex-1 px-3 py-4 space-y-1">
                {NAV.map(({ to, label, icon: Icon, testid }) => (
                    <NavLink
                        key={to}
                        to={to}
                        end={to === "/"}
                        onClick={onNavigate}
                        data-testid={testid}
                        className={({ isActive }) => cn(
                            "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors",
                            isActive
                                ? "bg-slate-800 text-white"
                                : "text-slate-400 hover:text-white hover:bg-slate-800/50"
                        )}
                    >
                        {({ isActive }) => (
                            <>
                                <Icon className={cn("h-4 w-4", isActive && "text-sky-400")} />
                                <span>{label}</span>
                            </>
                        )}
                    </NavLink>
                ))}
            </nav>

            <div className="px-4 py-4 border-t border-slate-800 space-y-3">
                <div className="flex items-start gap-2.5">
                    <span className="relative flex h-2.5 w-2.5 mt-1.5">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-60" />
                        <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" />
                    </span>
                    <div className="min-w-0">
                        <div className="flex items-center gap-1.5 text-white text-sm font-semibold">
                            <Activity className="h-3.5 w-3.5" /> Davis WeatherLink
                        </div>
                        <div className="text-[11px] text-emerald-400 font-medium">
                            {sync?.connected ? "Connected" : "Offline"}
                        </div>
                        <div className="text-[11px] text-slate-500" data-testid="sidebar-last-update">
                            Last update: {timeAgo(sync?.last_sync)}
                        </div>
                    </div>
                </div>
                <button
                    data-testid="sidebar-refresh"
                    onClick={() => syncMut.mutate()}
                    disabled={syncMut.isPending}
                    className="w-full text-xs bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-md py-2 flex items-center justify-center gap-2 transition-colors"
                >
                    <RefreshCcw className={cn("h-3.5 w-3.5", syncMut.isPending && "animate-spin")} />
                    {syncMut.isPending ? "Refreshing…" : "Refresh now"}
                </button>
            </div>
        </div>
    );
}

export default function Layout({ children }) {
    const [open, setOpen] = useState(false);
    const location = useLocation();
    const currentTitle = NAV.find(
        (n) => n.to === location.pathname || (n.to !== "/" && location.pathname.startsWith(n.to))
    )?.label || "Station";

    return (
        <div className="min-h-screen bg-[var(--fw-content-bg)]">
            {/* Desktop sidebar */}
            <aside className="hidden lg:flex fixed inset-y-0 left-0 w-64 z-30">
                <SidebarInner />
            </aside>

            {/* Mobile top bar */}
            <header className="lg:hidden sticky top-0 z-40 bg-[var(--fw-nav-bg)] text-white flex items-center justify-between px-4 h-14 border-b border-slate-800">
                <button
                    onClick={() => setOpen(true)}
                    data-testid="mobile-nav-open"
                    className="p-2 -ml-2"
                    aria-label="Open navigation"
                >
                    <Menu className="h-5 w-5" />
                </button>
                <div className="flex items-center gap-2">
                    <Sprout className="h-4 w-4 text-emerald-400" />
                    <span className="font-semibold" style={{ fontFamily: "Outfit" }}>
                        {currentTitle}
                    </span>
                </div>
                <div className="w-8" />
            </header>

            {/* Mobile drawer */}
            {open && (
                <div className="lg:hidden fixed inset-0 z-50 flex">
                    <div
                        className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
                        onClick={() => setOpen(false)}
                    />
                    <div className="relative w-72 max-w-[80%] h-full">
                        <button
                            onClick={() => setOpen(false)}
                            data-testid="mobile-nav-close"
                            className="absolute top-4 right-4 text-slate-400 hover:text-white z-10"
                            aria-label="Close navigation"
                        >
                            <X className="h-5 w-5" />
                        </button>
                        <SidebarInner onNavigate={() => setOpen(false)} />
                    </div>
                </div>
            )}

            <main className="lg:ml-64 px-4 py-5 sm:px-6 lg:px-8 lg:py-8 max-w-[1500px] mx-auto">
                {children}
            </main>
        </div>
    );
}
